import { useState } from 'react';
import type { GeometryQuestion } from '../data/geometryTestNY';

interface GeometryTestCardProps {
  testTitle: string;
  stateName: string;
  stateColor: string;
  questions: GeometryQuestion[];
  onClose: () => void;
}

export default function GeometryTestCard({ testTitle, stateName, stateColor, questions, onClose }: GeometryTestCardProps) {
  const [selectedAnswers, setSelectedAnswers] = useState<Record<number, number>>({});
  const [showSolutions, setShowSolutions] = useState<Record<number, boolean>>({});
  const [submitted, setSubmitted] = useState(false);
  const [currentFilter, setCurrentFilter] = useState<'all' | 'correct' | 'incorrect'>('all');

  const handleSelect = (qIndex: number, optIndex: number) => {
    if (submitted) return;
    setSelectedAnswers(prev => ({ ...prev, [qIndex]: optIndex }));
  };

  const toggleSolution = (qIndex: number) => {
    setShowSolutions(prev => ({ ...prev, [qIndex]: !prev[qIndex] }));
  };

  const handleSubmit = () => {
    setSubmitted(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleReset = () => {
    setSelectedAnswers({});
    setShowSolutions({});
    setSubmitted(false);
    setCurrentFilter('all');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const score = questions.reduce((acc, q, i) => acc + (selectedAnswers[i] === q.correctAnswer ? 1 : 0), 0);
  const totalPoints = questions.reduce((acc, q) => acc + q.points, 0);
  const earnedPoints = questions.reduce((acc, q, i) => acc + (selectedAnswers[i] === q.correctAnswer ? q.points : 0), 0);
  const percentage = Math.round((score / questions.length) * 100);
  const answeredCount = Object.keys(selectedAnswers).length;

  const getScoreBg = () => {
    if (percentage >= 85) return 'from-emerald-500 to-teal-600';
    if (percentage >= 65) return 'from-amber-500 to-orange-500';
    return 'from-red-500 to-rose-600';
  };

  const getScoreMessage = () => {
    if (percentage >= 85) return 'Outstanding! Geometry mastered!';
    if (percentage >= 65) return 'Good work! Review the missed questions.';
    if (percentage >= 50) return 'Keep practicing — you\'re improving!';
    return 'Don\'t give up! Review the lessons and try again.';
  };

  const filteredIndices = questions.map((_, i) => i).filter(i => {
    if (currentFilter === 'all') return true;
    if (currentFilter === 'correct') return submitted && selectedAnswers[i] === questions[i].correctAnswer;
    if (currentFilter === 'incorrect') return submitted && selectedAnswers[i] !== undefined && selectedAnswers[i] !== questions[i].correctAnswer;
    return true;
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50">
      {/* Header */}
      <div className={`bg-gradient-to-r ${stateColor} text-white sticky top-0 z-40 shadow-lg`}>
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 flex items-center justify-center bg-white/20 rounded-lg">
              <i className="ri-file-list-3-fill text-xl"></i>
            </div>
            <div>
              <h1 className="text-lg font-bold">{testTitle}</h1>
              <p className="text-white/80 text-xs">{questions.length} questions · {totalPoints} points · Mr. Augustine's Math Academy</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {!submitted && (
              <span className="text-sm text-white/80 whitespace-nowrap">{answeredCount}/{questions.length} answered</span>
            )}
            <button
              onClick={onClose}
              className="px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg text-sm font-medium transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-arrow-left-line mr-1"></i>Back
            </button>
          </div>
        </div>
        {!submitted && (
          <div className="max-w-5xl mx-auto px-4 pb-3">
            <div className="w-full bg-white/20 rounded-full h-1.5">
              <div
                className="bg-white rounded-full h-1.5 transition-all duration-300"
                style={{ width: `${(answeredCount / questions.length) * 100}%` }}
              />
            </div>
          </div>
        )}
      </div>

      <div className="max-w-5xl mx-auto px-4 py-8">
        {/* Score Summary */}
        {submitted && (
          <div className={`bg-gradient-to-r ${getScoreBg()} rounded-2xl p-8 text-white mb-8 shadow-xl`}>
            <div className="flex flex-col md:flex-row items-center gap-6">
              <div className="text-center">
                <div className="text-7xl font-black">{percentage}%</div>
                <div className="text-white/80 text-sm mt-1">Score</div>
              </div>
              <div className="flex-1">
                <h2 className="text-2xl font-bold mb-2">{getScoreMessage()}</h2>
                <div className="grid grid-cols-3 gap-4 mt-4">
                  <div className="bg-white/20 rounded-xl p-3 text-center">
                    <div className="text-2xl font-bold">{score}</div>
                    <div className="text-xs text-white/80">Correct</div>
                  </div>
                  <div className="bg-white/20 rounded-xl p-3 text-center">
                    <div className="text-2xl font-bold">{questions.length - score}</div>
                    <div className="text-xs text-white/80">Incorrect</div>
                  </div>
                  <div className="bg-white/20 rounded-xl p-3 text-center">
                    <div className="text-2xl font-bold">{earnedPoints}/{totalPoints}</div>
                    <div className="text-xs text-white/80">Points</div>
                  </div>
                </div>
                <div className="mt-4 flex gap-3 flex-wrap">
                  <button
                    onClick={handleReset}
                    className="px-5 py-2 bg-white text-gray-800 rounded-lg font-bold text-sm hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-refresh-line mr-1"></i>Retake Test
                  </button>
                  <button
                    onClick={() => setShowSolutions(Object.fromEntries(questions.map((_, i) => [i, true])))}
                    className="px-5 py-2 bg-white/20 hover:bg-white/30 rounded-lg font-bold text-sm transition-colors whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-eye-line mr-1"></i>Show All Solutions
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Filter Tabs */}
        {submitted && (
          <div className="flex gap-2 mb-6 flex-wrap">
            {(['all', 'correct', 'incorrect'] as const).map(f => (
              <button
                key={f}
                onClick={() => setCurrentFilter(f)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                  currentFilter === f
                    ? f === 'correct' ? 'bg-emerald-600 text-white' : f === 'incorrect' ? 'bg-red-500 text-white' : 'bg-teal-600 text-white'
                    : 'bg-white text-gray-600 hover:bg-gray-100'
                }`}
              >
                {f === 'all' && `All (${questions.length})`}
                {f === 'correct' && `Correct (${score})`}
                {f === 'incorrect' && `Incorrect (${questions.length - score})`}
              </button>
            ))}
          </div>
        )}

        {/* Questions */}
        <div className="space-y-6">
          {filteredIndices.map(qIndex => {
            const q = questions[qIndex];
            const selected = selectedAnswers[qIndex];
            const isCorrect = submitted && selected === q.correctAnswer;
            const isWrong = submitted && selected !== undefined && selected !== q.correctAnswer;
            const showSol = showSolutions[qIndex];

            return (
              <div
                key={qIndex}
                className={`bg-white rounded-2xl shadow-md border-2 transition-all ${
                  isCorrect ? 'border-emerald-300' : isWrong ? 'border-red-300' : 'border-gray-100'
                }`}
              >
                <div className={`px-6 py-4 rounded-t-2xl flex items-start gap-4 ${
                  isCorrect ? 'bg-emerald-50' : isWrong ? 'bg-red-50' : 'bg-gray-50'
                }`}>
                  <div className={`w-8 h-8 flex items-center justify-center rounded-full text-sm font-bold flex-shrink-0 ${
                    isCorrect ? 'bg-emerald-500 text-white' : isWrong ? 'bg-red-500 text-white' : 'bg-teal-600 text-white'
                  }`}>
                    {isCorrect ? <i className="ri-check-line"></i> : isWrong ? <i className="ri-close-line"></i> : qIndex + 1}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className="text-xs font-semibold text-teal-700 bg-teal-100 px-2 py-0.5 rounded-full whitespace-nowrap">{q.topic}</span>
                      <span className="text-xs text-gray-500 whitespace-nowrap">{q.standard}</span>
                      <span className="text-xs font-medium text-gray-500 whitespace-nowrap">{q.points} pts</span>
                    </div>
                    <p className="text-gray-900 font-semibold leading-relaxed">{q.question}</p>
                  </div>
                </div>

                <div className="px-6 py-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {q.options.map((opt, oIndex) => {
                      const isSelected = selected === oIndex;
                      const isCorrectOpt = oIndex === q.correctAnswer;
                      let btnClass = 'border-2 border-gray-200 bg-white text-gray-700 hover:border-teal-300 hover:bg-teal-50';
                      if (submitted) {
                        if (isCorrectOpt) btnClass = 'border-2 border-emerald-500 bg-emerald-50 text-emerald-800';
                        else if (isSelected && !isCorrectOpt) btnClass = 'border-2 border-red-400 bg-red-50 text-red-800';
                        else btnClass = 'border-2 border-gray-100 bg-gray-50 text-gray-500';
                      } else if (isSelected) {
                        btnClass = 'border-2 border-teal-500 bg-teal-50 text-teal-800';
                      }

                      return (
                        <button
                          key={oIndex}
                          onClick={() => handleSelect(qIndex, oIndex)}
                          className={`w-full text-left px-4 py-3 rounded-xl transition-all text-sm font-medium cursor-pointer ${btnClass}`}
                        >
                          <span className="font-bold mr-2">{String.fromCharCode(65 + oIndex)}.</span>
                          {opt}
                          {submitted && isCorrectOpt && <i className="ri-check-line ml-2 text-emerald-600"></i>}
                          {submitted && isSelected && !isCorrectOpt && <i className="ri-close-line ml-2 text-red-500"></i>}
                        </button>
                      );
                    })}
                  </div>

                  <div className="mt-4">
                    <button
                      onClick={() => toggleSolution(qIndex)}
                      className={`text-sm font-medium px-4 py-2 rounded-lg transition-colors whitespace-nowrap cursor-pointer ${
                        showSol ? 'bg-amber-100 text-amber-800' : 'bg-gray-100 text-gray-600 hover:bg-amber-50 hover:text-amber-700'
                      }`}
                    >
                      <i className={`${showSol ? 'ri-eye-off-line' : 'ri-eye-line'} mr-1`}></i>
                      {showSol ? 'Hide Solution' : 'Show Solution'}
                    </button>
                  </div>

                  {showSol && (
                    <div className="mt-4 bg-gradient-to-br from-amber-50 to-yellow-50 border border-amber-200 rounded-xl p-5">
                      <div className="flex items-center gap-2 mb-3">
                        <i className="ri-star-fill text-amber-500"></i>
                        <span className="font-bold text-amber-800 text-sm">Step-by-Step Solution</span>
                      </div>
                      <ol className="space-y-2">
                        {q.solution.map((step, sIndex) => (
                          <li key={sIndex} className="flex items-start gap-3">
                            <span className="w-6 h-6 flex items-center justify-center bg-amber-500 text-white rounded-full text-xs font-bold flex-shrink-0 mt-0.5">
                              {sIndex + 1}
                            </span>
                            <p className="text-gray-800 text-sm leading-relaxed">{step}</p>
                          </li>
                        ))}
                      </ol>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Submit Button */}
        {!submitted && (
          <div className="mt-10 flex flex-col items-center gap-4">
            <div className="text-sm text-gray-500">
              {answeredCount < questions.length
                ? `${questions.length - answeredCount} question(s) unanswered`
                : 'All questions answered — ready to submit!'}
            </div>
            <button
              onClick={handleSubmit}
              disabled={answeredCount === 0}
              className={`px-10 py-4 rounded-xl font-bold text-lg shadow-lg transition-all whitespace-nowrap cursor-pointer ${
                answeredCount > 0
                  ? 'bg-gradient-to-r from-teal-600 to-emerald-600 text-white hover:from-teal-700 hover:to-emerald-700 hover:shadow-xl'
                  : 'bg-gray-200 text-gray-400 cursor-not-allowed'
              }`}
            >
              <i className="ri-send-plane-fill mr-2"></i>
              Submit Test ({answeredCount}/{questions.length})
            </button>
          </div>
        )}

        {submitted && (
          <div className="mt-10 flex justify-center gap-4 flex-wrap">
            <button
              onClick={handleReset}
              className="px-8 py-3 bg-teal-600 text-white rounded-xl font-bold hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-refresh-line mr-2"></i>Retake Test
            </button>
            <button
              onClick={onClose}
              className="px-8 py-3 bg-gray-200 text-gray-700 rounded-xl font-bold hover:bg-gray-300 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-arrow-left-line mr-2"></i>Back to Tests
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
